import { useState, useRef, useEffect } from 'react';
import { Search, User, Phone, MapPin, AlertTriangle, ChevronLeft, Loader2, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';

function fmt(n) {
  return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function CustomerInfoCard({ info, onContinue }) {
  const overLimit = info.credit_exceeded;
  const hasOverdue = info.has_overdue;
  const creditPct = info.credit_limit > 0 ? Math.min(100, (info.credit / info.credit_limit) * 100) : 0;
  const lastOrder = info.last_order;

  return (
    <div className="space-y-4">
      {/* Warnings */}
      {(overLimit || hasOverdue) && (
        <div className="space-y-2">
          {overLimit && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>تجاوز حد الائتمان — المتاح: {fmt(info.available_credit)} {cur()}</span>
            </div>
          )}
          {hasOverdue && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-700 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>يوجد {info.overdue_invoices.length} فاتورة متأخرة غير مدفوعة</span>
            </div>
          )}
        </div>
      )}

      {/* Customer card */}
      <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-primary font-bold text-lg">{info.name?.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-foreground text-base">{info.name}</p>
            {(info.phone || info.mobile) && (
              <div className="flex items-center gap-1 mt-0.5">
                <Phone className="w-3 h-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{info.phone || info.mobile}</p>
              </div>
            )}
            {info.city && (
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">{info.city}</p>
              </div>
            )}
          </div>
        </div>

        {/* Credit info */}
        {info.credit_limit > 0 && (
          <div className="bg-muted/50 rounded-xl p-3 space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>حد الائتمان</span>
              <span className="font-semibold text-foreground">{fmt(info.credit_limit)} {cur()}</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${creditPct >= 90 ? 'bg-red-500' : creditPct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${creditPct}%` }}
              />
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">مستخدم: <span className="text-foreground font-medium">{fmt(info.credit)} {cur()}</span></span>
              <span className={`font-semibold ${overLimit ? 'text-red-600' : 'text-emerald-600'}`}>
                متاح: {fmt(info.available_credit)} {cur()}
              </span>
            </div>
          </div>
        )}

        {/* Last order */}
        {lastOrder && (
          <div className="flex justify-between text-xs bg-blue-50 rounded-xl p-2.5">
            <span className="text-muted-foreground">آخر طلب</span>
            <span className="font-medium text-foreground">
              {new Date(lastOrder.date_order).toLocaleDateString('ar-EG', { day: 'numeric', month: 'short' })} — {fmt(lastOrder.amount_total)} {cur()}
            </span>
          </div>
        )}

        {info.__offline && (
          <div className="text-[11px] text-amber-600 bg-amber-50 rounded-xl p-2 text-center">
            بيانات مبدئية (بدون إنترنت) — هتتأكد بعد المزامنة
          </div>
        )}
      </div>

      <Button
        onClick={onContinue}
        className="w-full bg-primary h-12 text-base font-semibold rounded-xl gap-2"
      >
        تابع — إضافة منتجات
        <ChevronLeft className="w-5 h-5" />
      </Button>
    </div>
  );
}

export default function CustomerSelector({ selectedCustomer, customerInfo, onSelect, onContinue, scopeRepUid }) {
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const timerRef = useRef(null);
  const { odooSession } = useOdooAuth();

  useEffect(() => {
    // Load recent customers on mount (and whenever the rep scope changes).
    fetchCustomers('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRepUid]);

  const fetchCustomers = async (q) => {
    setLoading(true);
    const params = {
      search: q || undefined, limit: q ? 20 : 10,
      sort_by: q ? undefined : 'last_order',
      salesman_uid: odooSession?.uid,
      // Keep the order customer list scoped to the rep's dealer categories
      // (same as the Customers screen) even while searching — no old-order
      // customers from outside the rep's categories.
      strict_category: 1 };
    // When dispensing a gift "from" a chosen rep, scope to that rep's
    // customers only (their dealer categories).
    if (scopeRepUid) params.rep_uid = scopeRepUid;
    // '/sellgo_partners' actually filters by the search term (and scopes by
    // the rep's categories). The legacy '/customers' ignores `search`, so
    // typing a name returned an unfiltered list. Fall back to it only if the
    // SellGo endpoint isn't available. The whole thing is wrapped so a network
    // failure offline can never leave the list spinner stuck — the read-through
    // cache serves the last saved customers, and worst case we just stop loading.
    try {
      let res;
      try {
        res = await api.get('/sellgo_v5_customer_search', params);
      } catch {
        res = await api.get('/customers', params);
      }
      if (res?.success) setCustomers(res.data || []);
    } catch {
      /* offline + uncached → keep current list */
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchCustomers(val), 350);
  };

  const handleSelect = async (customer) => {
    setLoadingInfo(true);
    let info = null;
    try {
      // Read-through cache serves this instantly if the customer was opened
      // online before; offline-and-uncached it fast-fails into the catch.
      const res = await api.get(`/customers/${customer.id}/order-info`);
      info = res?.success ? res.data : null;
    } catch {
      info = null;
    }
    if (!info) {
      // Offline / uncached → build a minimal profile from the cached customer
      // row so order creation can still proceed (default product prices apply).
      const creditLimit = Number(customer.credit_limit || 0);
      const credit = Number(customer.credit || 0);
      info = {
        id: customer.id,
        name: customer.name,
        phone: customer.phone || '',
        mobile: customer.mobile || '',
        city: customer.city || '',
        credit,
        credit_limit: creditLimit,
        available_credit: Math.max(0, creditLimit - credit),
        credit_exceeded: false,
        has_overdue: false,
        overdue_invoices: [],
        last_order: null,
        __offline: true,
      };
    }
    onSelect(customer, info);
    setLoadingInfo(false);
  };

  if (loadingInfo) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">جاري تحميل بيانات العميل...</p>
      </div>
    );
  }

  if (selectedCustomer && customerInfo) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-foreground text-lg">العميل المختار</h2>
          <button onClick={() => onSelect(null, null)} className="text-sm text-primary font-medium">تغيير</button>
        </div>
        <CustomerInfoCard info={customerInfo} onContinue={onContinue} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
          <User className="w-4 h-4 text-primary" />
        </div>
        <h2 className="font-bold text-foreground text-lg">اختيار العميل</h2>
      </div>
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => handleSearch(e.target.value)}
          placeholder="بحث بالاسم أو الهاتف..."
          className="pr-10 rounded-xl bg-card"
          autoFocus
        />
      </div>

      {!search && <p className="text-xs text-muted-foreground font-medium px-1">آخر العملاء</p>}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : customers.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">لا يوجد عملاء مطابقون</p>
      ) : (
        <div className="space-y-2">
          {customers.map(c => {
            const credit = Number(c.credit || 0);
            return (
              <div key={c.id}
                onClick={() => handleSelect(c)}
                className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3 cursor-pointer hover:border-primary/40 hover:shadow-sm transition-all active:scale-[0.99]"
              >
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold text-lg">{c.name?.charAt(0) || '؟'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">{c.name}</p>
                  <div className="flex items-center gap-2.5 text-xs text-muted-foreground mt-0.5">
                    {(c.phone || c.mobile) && (
                      <span className="flex items-center gap-1 truncate"><Phone className="w-3 h-3 flex-shrink-0" />{c.phone || c.mobile}</span>
                    )}
                    {c.city && <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 flex-shrink-0" />{c.city}</span>}
                  </div>
                  {credit > 0 && (
                    <span className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-bold text-red-600 bg-red-50 border border-red-100 px-2 py-0.5 rounded-full">
                      مديونية: {credit.toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur('ج.م')}
                    </span>
                  )}
                </div>
                <ChevronLeft className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}